import { useState, useEffect, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { useMsal } from "@azure/msal-react";
import { apiScope, useAuthenticatedApi } from "../auth";
import {
  getNotifications,
  getUnreadCount,
  markAsRead as apiMarkAsRead,
  markAllAsRead as apiMarkAllAsRead,
  deleteNotification as apiDeleteNotification,
  clearAllNotifications as apiClearAllNotifications,
} from "../api/notificationsApi";

const SEARCH_API_URL = import.meta.env.VITE_API_SEARCH_URL;
// In dev, use relative path so Vite proxy handles it (avoids CORS with credentials)
const HUB_URL = import.meta.env.DEV
  ? '/hub/notifications'
  : `${SEARCH_API_URL}/hub/notifications`;
const API_SCOPE = Array.isArray(apiScope) ? apiScope : [];

export function useNotifications() {
  const { instance, accounts } = useMsal();
  const { callProtectedApi } = useAuthenticatedApi();
  const callRef = useRef(callProtectedApi);
  useEffect(() => { callRef.current = callProtectedApi; }, [callProtectedApi]);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(true);
  const connectionRef                     = useRef(null);

  // ── Load initial notifications ──────────────────────────────────────────────
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNotifications(callRef.current);
      setNotifications(data?.notifications ?? []);
      setUnreadCount(data?.unreadCount ?? 0);
    } catch (err) {
      console.error("[Notifications] Failed to load:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── SignalR connection ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!SEARCH_API_URL || !accounts?.length || !API_SCOPE.length) return;

    const getToken = async () => {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: API_SCOPE,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    };

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: getToken,
        // Skip ServerSentEvents — Azure App Service's reverse proxy buffers SSE responses
        // instead of streaming them, causing a 404 "connection not found" error.
        // WebSockets (requires Web Sockets enabled in App Service config) is tried first;
        // Long Polling is the reliable fallback until Web Sockets is turned on.
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("NewNotification", (notification) => {
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    });

    connection.start()
      .then(() => console.info("[SignalR] Connected to notifications hub"))
      .catch((err) => console.error("[SignalR] Connection failed:", err));

    connectionRef.current = connection;

    return () => {
      connection.stop();
    };
  }, [instance, accounts]);

  // Load on mount
  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const markAsRead = useCallback(async (id) => {
    try {
      await apiMarkAsRead(callRef.current, id);
      setNotifications((prev) =>
        prev.map((n) => n.id === id ? { ...n, isRead: true } : n)
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("[Notifications] markAsRead failed:", err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await apiMarkAllAsRead(callRef.current);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("[Notifications] markAllAsRead failed:", err);
    }
  }, []);

  const removeNotification = useCallback(async (id) => {
    try {
      await apiDeleteNotification(callRef.current, id);
      setNotifications((prev) => {
        const removed = prev.find((n) => n.id === id);
        if (removed && !removed.isRead) setUnreadCount((c) => Math.max(0, c - 1));
        return prev.filter((n) => n.id !== id);
      });
    } catch (err) {
      console.error("[Notifications] delete failed:", err);
    }
  }, []);

  const clearAll = useCallback(async () => {
    try {
      await apiClearAllNotifications(callRef.current);
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error("[Notifications] clearAll failed:", err);
    }
  }, []);

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead, removeNotification, clearAll };
}
